import { DataServiceDappMessages } from '../dapp/data-service-dapp-messages';
import { CachedTokenProvider, DEFAULT_TOKEN_LIFETIME, DefaultTokenProvider, } from '../../auth/token-provider';
import { DataServiceDappNotificationSubscriptions } from '../dapp/data-service-dapp-notification-subscriptions';
import { Duration } from 'luxon';
import { DappMessagesFacade } from '../dapp/dapp-messages-facade';
import { DappsImpl } from '../dapp/dapp';
import { TokenStore } from '../../auth/token-store';
import { DappAddressesFacade } from '../dapp/dapp-addresses-facade';
import { AggregateSequentialIdentityResolver, FirstFoundFastIdentityResolver, FirstFoundIdentityResolver, } from '../identity/identity-resolvers';
import { DataServiceWallets } from '../wallet/data-service-wallets';
import { EncryptionKeysStore } from '../../encryption/encryption-keys-store';
import { DataServiceMessaging } from '../messaging/data-service-messaging';
import { MessagingFacade } from '../messaging/messaging-facade';
import { IllegalArgumentError } from '../../sdk/errors';
import { DataServiceDappAddresses } from '../dapp/data-service-dapp-addresses';
import { DataServiceDappNotificationTypes } from '../dapp/data-service-dapp-notification-types';
import { DataServiceApiFactory } from '../../dialect-cloud-api/data-service-api-factory';
export class InternalDialectSdk {
    constructor(config, threads, dapps, wallet, identity, tokenProvider, encryptionKeysProvider, blockchainSdk) {
        this.config = config;
        this.threads = threads;
        this.dapps = dapps;
        this.wallet = wallet;
        this.identity = identity;
        this.tokenProvider = tokenProvider;
        this.encryptionKeysProvider = encryptionKeysProvider;
        this.blockchainSdk = blockchainSdk;
    }
    get info() {
        return {
            supportsEndToEndEncryption: this.encryptionKeysProvider.isAvailable(),
            hasValidAuthentication: this.tokenProvider.hasValidCachedToken(),
        };
    }
}
export class DialectSdkFactory {
    constructor(config, blockchainFactory) {
        this.config = config;
        this.blockchainFactory = blockchainFactory;
    }
    static logConfiguration(config) {
        if (config.environment !== 'production') {
            console.log(`Initializing Dialect SDK using configuration:
  Dialect cloud settings:
    URL: ${config.dialectCloud.url}
  `);
        }
    }
    create() {
        const config = this.initializeConfig();
        DialectSdkFactory.logConfiguration(config);
        const blockchainSdk = this.blockchainFactory.create(config);
        const tokenProvider = this.initializeTokenProvider(config, blockchainSdk);
        const dataServiceApi = this.initializeDataServiceApi(config.dialectCloud, tokenProvider);
        const messaging = this.initializeMessagingApi(dataServiceApi, blockchainSdk);
        const dapps = this.initializeDappApi(dataServiceApi, blockchainSdk);
        const wallet = new DataServiceWallets(blockchainSdk.authenticationFacade.subject(), dataServiceApi.walletAddresses, dataServiceApi.walletDappAddresses, dataServiceApi.walletMessages, dataServiceApi.walletNotificationSubscriptions, dataServiceApi.pushNotificationSubscriptions);
        const identity = this.createIdentityResolver(config.identity);
        return new InternalDialectSdk(config, messaging, dapps, wallet, identity, tokenProvider, blockchainSdk.encryptionKeysProvider, blockchainSdk);
    }
    initializeTokenProvider(config, { authenticationFacade }) {
        const defaultTokenProvider = new DefaultTokenProvider(Duration.fromObject({
            minutes: config.dialectCloud.tokenLifetimeMinutes,
        }), authenticationFacade.tokenGenerator);
        return new CachedTokenProvider(defaultTokenProvider, config.dialectCloud.tokenStore, authenticationFacade.authenticator.parser, authenticationFacade.authenticator.validator, authenticationFacade.subject());
    }
    initializeDataServiceApi(config, tokenProvider) {
        return DataServiceApiFactory.create(config.url, tokenProvider);
    }
    initializeMessagingApi(dataServiceApi, blockchainSdk) {
        const messagings = [];
        const dataServiceMessaging = new DataServiceMessaging(blockchainSdk.authenticationFacade.subject(), dataServiceApi.threads, blockchainSdk.encryptionKeysProvider);
        messagings.push(dataServiceMessaging);
        if (blockchainSdk.messaging) {
            messagings.push(blockchainSdk.messaging);
        }
        return new MessagingFacade(messagings);
    }
    initializeDappApi(dataServiceApi, blockchainSdk) {
        const dappAddresses = this.createDappAddresses(dataServiceApi, blockchainSdk);
        const dappNotificationTypes = new DataServiceDappNotificationTypes(dataServiceApi.dappNotificationTypes);
        const dappNotificationSubscriptions = new DataServiceDappNotificationSubscriptions(dataServiceApi.dappNotificationSubscriptions);
        const dappMessages = this.createDappMessages(dataServiceApi, blockchainSdk);
        return new DappsImpl(dappAddresses, dappMessages, dappNotificationTypes, dappNotificationSubscriptions, dataServiceApi.dapps);
    }
    createDappAddresses(dataServiceApi, blockchainSdk) {
        const dappAddresses = [];
        const dataServiceDappAddresses = new DataServiceDappAddresses(dataServiceApi.dapps);
        dappAddresses.push(dataServiceDappAddresses);
        if (blockchainSdk.dappAddresses) {
            dappAddresses.push(blockchainSdk.dappAddresses);
        }
        return new DappAddressesFacade(dappAddresses);
    }
    createDappMessages(dataServiceApi, blockchainSdk) {
        const dappMessages = [];
        const dataServiceDappMessages = new DataServiceDappMessages(dataServiceApi.dapps);
        dappMessages.push(dataServiceDappMessages);
        if (blockchainSdk.dappMessages) {
            dappMessages.push(blockchainSdk.dappMessages);
        }
        return new DappMessagesFacade(dappMessages);
    }
    createIdentityResolver(config) {
        if (config.strategy === 'first-found') {
            return new FirstFoundIdentityResolver(config.resolvers);
        }
        if (config.strategy === 'first-found-fast') {
            return new FirstFoundFastIdentityResolver(config.resolvers);
        }
        if (config.strategy === 'aggregate-sequential') {
            return new AggregateSequentialIdentityResolver(config.resolvers);
        }
        throw new IllegalArgumentError(`Unknown identity strategy ${config.strategy}`);
    }
    initializeConfig() {
        var _a;
        const environment = (_a = this.config.environment) !== null && _a !== void 0 ? _a : 'production';
        const encryptionKeysStore = this.createEncryptionKeysStore();
        const identity = this.createIdentityConfig();
        return {
            environment,
            dialectCloud: this.initializeDialectCloudConfig(),
            encryptionKeysStore,
            identity,
        };
    }
    createEncryptionKeysStore() {
        const encryptionKeysStoreConfig = this.config.encryptionKeysStore;
        if (encryptionKeysStoreConfig &&
            encryptionKeysStoreConfig instanceof EncryptionKeysStore) {
            return encryptionKeysStoreConfig;
        }
        if (encryptionKeysStoreConfig === 'in-memory') {
            return EncryptionKeysStore.createInMemory();
        }
        if (encryptionKeysStoreConfig === 'session-storage') {
            return EncryptionKeysStore.createSessionStorage();
        }
        if (encryptionKeysStoreConfig === 'local-storage') {
            return EncryptionKeysStore.createLocalStorage();
        }
        return EncryptionKeysStore.createInMemory();
    }
    initializeDialectCloudConfig() {
        var _a, _b;
        const baseConfig = {
            environment: 'production',
            url: 'https://dialectapi.to',
            tokenStore: this.createTokenStore(),
            tokenLifetimeMinutes: this.createTokenLifetime(),
        };
        const environment = this.config.environment;
        if (environment) {
            baseConfig.environment = environment;
        }
        if (environment === 'production') {
            baseConfig.url = 'https://dialectapi.to';
        }
        if (environment === 'development') {
            baseConfig.url = 'https://dev.dialectapi.to';
        }
        if (environment === 'local-development') {
            baseConfig.url = 'http://localhost:8080';
        }
        const dialectCloudEnvironment = (_a = this.config.dialectCloud) === null || _a === void 0 ? void 0 : _a.environment;
        if (dialectCloudEnvironment) {
            baseConfig.environment = dialectCloudEnvironment;
        }
        if (dialectCloudEnvironment === 'production') {
            baseConfig.url = 'https://dialectapi.to';
        }
        if (dialectCloudEnvironment === 'development') {
            baseConfig.url = 'https://dev.dialectapi.to';
        }
        if (dialectCloudEnvironment === 'local-development') {
            baseConfig.url = 'http://localhost:8080';
        }
        if ((_b = this.config.dialectCloud) === null || _b === void 0 ? void 0 : _b.url) {
            baseConfig.url = this.config.dialectCloud.url;
        }
        return baseConfig;
    }
    createTokenLifetime() {
        var _a, _b;
        return ((_b = (_a = this.config.dialectCloud) === null || _a === void 0 ? void 0 : _a.tokenLifetimeMinutes) !== null && _b !== void 0 ? _b : DEFAULT_TOKEN_LIFETIME.toMillis() / 1000 / 60);
    }
    createTokenStore() {
        var _a;
        const tokenStoreConfig = (_a = this.config.dialectCloud) === null || _a === void 0 ? void 0 : _a.tokenStore;
        if (tokenStoreConfig && tokenStoreConfig instanceof TokenStore) {
            return tokenStoreConfig;
        }
        if (tokenStoreConfig === 'in-memory') {
            return TokenStore.createInMemory();
        }
        if (tokenStoreConfig === 'session-storage') {
            return TokenStore.createSessionStorage();
        }
        if (tokenStoreConfig === 'local-storage') {
            return TokenStore.createLocalStorage();
        }
        return TokenStore.createInMemory();
    }
    createIdentityConfig() {
        const identityConfig = {
            strategy: 'first-found',
            resolvers: [],
        };
        if (!this.config.identity) {
            return identityConfig;
        }
        if (this.config.identity.strategy) {
            identityConfig.strategy = this.config.identity.strategy;
        }
        if (this.config.identity.resolvers) {
            identityConfig.resolvers = this.config.identity.resolvers;
        }
        return identityConfig;
    }
}
//# sourceMappingURL=sdk-factory.js.map